/*
 * Where: src/hooks/useReadingStatus.js
 * What: Reading state management for real NFC UID flow and communication loading.
 * Why: Keep UI concerns and NFC flow control separated for task 2 and task 4.
 */
import { useEffect, useRef, useState } from 'react';
import { getCitizenSupportByCardUid } from '../constants/citizenSupportByCardUid';
import { readCardUidViaExtension } from '../features/nfc/services/extensionBridgeClient';

export const READING_STATE = {
    idle: 'idle',
    waiting: 'waiting',
    communicating: 'communicating',
    ready: 'ready',
};

export const COMMUNICATING_DURATION_MS = 3000;
export const DEVELOPMENT_CARD_UID = 'AB12CD34EF56';

const STATUS_COPY = {
    [READING_STATE.idle]: {
        badge: '待機中',
        title: '読み取り待機中',
        detail: '読み取りを開始すると、カード完了通知まで待機表示を続けます。',
    },
    [READING_STATE.waiting]: {
        badge: '読み取り待機',
        title: '読み取り待機中',
        detail:
            'マイナンバーカードの読み取りをしてください。',
    },
    [READING_STATE.communicating]: {
        badge: '通信中',
        title: '通信中',
        detail: '受けられる行政サービスを照会しています。',
    },
    [READING_STATE.ready]: {
        badge: '確認可能',
        title: '確認準備完了',
        detail: '受けられる行政サービス候補を確認できます。',
    },
};

const PRIMARY_LABEL = {
    [READING_STATE.idle]: '読み取りを開始',
    [READING_STATE.waiting]: '読み取り待機中',
    [READING_STATE.communicating]: '通信中...',
    [READING_STATE.ready]: '再確認する',
};

const resolveReadErrorMessage = (error) => {
    if (!error || !error.code) {
        return '読み取りに失敗しました。リーダー接続と拡張機能設定を確認してください。';
    }
    if (error.code === 'BRIDGE_TIMEOUT') {
        return '読み取りがタイムアウトしました。カードをかざし直すか、拡張機能設定を確認してください。';
    }
    if (error.code === 'EMPTY_UID') {
        return 'カードUIDを取得できませんでした。カードを外して再度かざしてください。';
    }
    return '読み取りに失敗しました。リーダー接続と拡張機能設定を確認してください。';
};

export const useReadingStatus = () => {
    const [readingState, setReadingState] = useState(READING_STATE.idle);
    const [lastReadUid, setLastReadUid] = useState('');
    const [lastReadSource, setLastReadSource] = useState('');
    const [citizenSupport, setCitizenSupport] = useState(null);
    const [readError, setReadError] = useState('');
    const communicationTimerRef = useRef(null);
    const readRequestTokenRef = useRef(0);
    const readAbortControllerRef = useRef(null);

    useEffect(
        () => () => {
            readRequestTokenRef.current += 1;
            if (readAbortControllerRef.current) {
                readAbortControllerRef.current.abort();
            }
            if (communicationTimerRef.current) {
                clearTimeout(communicationTimerRef.current);
            }
        },
        []
    );

    const startCommunicatingPhase = (cardUid, source) => {
        const normalizedCardUid = String(cardUid || '').toUpperCase();
        setReadError('');
        setLastReadUid(normalizedCardUid);
        setLastReadSource(source);
        setCitizenSupport(getCitizenSupportByCardUid(normalizedCardUid));
        setReadingState(READING_STATE.communicating);
        if (communicationTimerRef.current) {
            clearTimeout(communicationTimerRef.current);
        }
        communicationTimerRef.current = setTimeout(() => {
            setReadingState(READING_STATE.ready);
        }, COMMUNICATING_DURATION_MS);
    };

    const startReading = async () => {
        if (readingState === READING_STATE.waiting || readingState === READING_STATE.communicating) {
            return;
        }

        const currentToken = readRequestTokenRef.current + 1;
        readRequestTokenRef.current = currentToken;
        if (readAbortControllerRef.current) {
            readAbortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        readAbortControllerRef.current = abortController;
        setReadError('');
        setReadingState(READING_STATE.waiting);

        try {
            const cardUid = await readCardUidViaExtension({
                signal: abortController.signal,
            });
            if (readRequestTokenRef.current !== currentToken) {
                return;
            }
            readAbortControllerRef.current = null;
            startCommunicatingPhase(cardUid, 'reader');
        } catch (error) {
            if (readRequestTokenRef.current !== currentToken) {
                return;
            }
            readAbortControllerRef.current = null;
            if (error && error.code === 'ABORTED') {
                return;
            }
            setReadingState(READING_STATE.idle);
            setReadError(resolveReadErrorMessage(error));
        }
    };

    const completeReadingForDevelopment = () => {
        if (readingState === READING_STATE.communicating) {
            return;
        }
        readRequestTokenRef.current += 1;
        if (readAbortControllerRef.current) {
            readAbortControllerRef.current.abort();
            readAbortControllerRef.current = null;
        }
        startCommunicatingPhase(DEVELOPMENT_CARD_UID, 'development');
    };

    return {
        readingState,
        statusCopy: STATUS_COPY[readingState],
        primaryLabel: PRIMARY_LABEL[readingState],
        lastReadUid,
        lastReadSource,
        citizenSupport,
        canShowSupportList:
            readingState === READING_STATE.ready &&
            Boolean(citizenSupport && citizenSupport.can_use_support),
        readError,
        isLoading:
            readingState === READING_STATE.waiting ||
            readingState === READING_STATE.communicating,
        canCompleteForDevelopment: readingState !== READING_STATE.communicating,
        isPrimaryDisabled:
            readingState === READING_STATE.waiting ||
            readingState === READING_STATE.communicating,
        startReading,
        completeReadingForDevelopment,
    };
};
